import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { MoreHorizontal, Pause, Trash2 } from 'lucide-react'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { useAbortCountSession } from '../hooks/use-count-session'
import { useCountUiStore } from '../store/count-ui-store'
import { safeAreaBottom } from '../lib/safe-area'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** Below this the expiry is called out rather than just stated. */
const EXPIRY_WARNING_MINUTES = 15

interface CountSessionMenuProps {
  restaurantId: number | null
  sessionId: string | null
  /** Only the capture screen offers "go to review"; the review screen already is there. */
  showReviewAction: boolean
  /** When the inactivity sweep would end the session — last activity plus the server's window. */
  expiresAt: string | null
}

/**
 * Replaces the header's back arrow, which silently left an open count with no hint that the
 * session stays open server-side. Leaving is now an explicit choice: pause (the session keeps
 * running and can be resumed) or abort (confirmed, discards the captures).
 *
 * It sits top *right* as an ellipsis, not top left as a hamburger: the top-left slot on a phone
 * reads as "back", which is exactly the silent exit this replaced, and an ellipsis says "more
 * about this thing" rather than "navigation". The name is spelled out for screen readers, since
 * the glyph itself carries none.
 *
 * The automatic expiry of an idle session is stated here as a wall-clock time, which is what the
 * counter can act on; it is computed server-side from the last activity, so the client never has
 * to know the configured window. No ticker of its own: the count layout already re-renders every
 * 30 s for the elapsed-time line, which is finer than a minute-resolution deadline needs.
 */
export function CountSessionMenu({
  restaurantId,
  sessionId,
  showReviewAction,
  expiresAt,
}: CountSessionMenuProps) {
  const { t } = useTranslation('inventory-count')
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [confirmingAbort, setConfirmingAbort] = useState(false)
  const abort = useAbortCountSession(restaurantId, sessionId)
  // Published so the capture screen below can pause detection: a scan landing behind the open
  // menu would beep and stack the quantity sheet underneath it.
  const setSessionMenuOpen = useCountUiStore((s) => s.setSessionMenuOpen)

  // The flag outlives this component, so a menu left open while the flow is left behind would
  // pause the scanner forever on the next visit.
  useEffect(() => () => setSessionMenuOpen(false), [setSessionMenuOpen])

  function openMenu() {
    setOpen(true)
    setSessionMenuOpen(true)
  }

  function close() {
    setOpen(false)
    setConfirmingAbort(false)
    setSessionMenuOpen(false)
  }

  const expiry = expiresAt != null ? new Date(expiresAt) : null
  const expiryTime =
    expiry != null && !Number.isNaN(expiry.getTime())
      ? format(expiry, 'HH:mm')
      : null
  const minutesLeft =
    expiry != null && expiryTime != null
      ? Math.round((expiry.getTime() - Date.now()) / 60000)
      : null
  const expirySoon = minutesLeft != null && minutesLeft < EXPIRY_WARNING_MINUTES

  function abortSession() {
    abort.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('start.aborted'))
        close()
        navigate({ to: '/inventur' })
      },
      onError: () => {
        toast.error(t('start.abortFailed'))
        setConfirmingAbort(false)
      },
    })
  }

  return (
    <Drawer open={open} onOpenChange={(next) => (next ? openMenu() : close())}>
      <DrawerTrigger asChild>
        <Button variant="ghost" size="icon-lg" aria-label={t('layout.menu')}>
          <MoreHorizontal className="h-5 w-5" />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[90svh]">
        {confirmingAbort ? (
          <>
            <DrawerHeader>
              <DrawerTitle>{t('layout.abortConfirmTitle')}</DrawerTitle>
              <DrawerDescription>
                {t('layout.abortConfirmBody')}
              </DrawerDescription>
            </DrawerHeader>
            <DrawerFooter style={safeAreaBottom('1rem')}>
              <Button
                variant="destructive"
                className="h-12"
                disabled={abort.isPending}
                onClick={abortSession}
              >
                {t('layout.abortConfirm')}
              </Button>
              <Button
                variant="outline"
                className="h-12"
                onClick={() => setConfirmingAbort(false)}
              >
                {t('layout.abortKeep')}
              </Button>
            </DrawerFooter>
          </>
        ) : (
          <>
            <DrawerHeader>
              <DrawerTitle>{t('layout.menuTitle')}</DrawerTitle>
              <DrawerDescription>{t('layout.menuHint')}</DrawerDescription>
              {expiryTime != null && sessionId != null && (
                <p
                  className={cn(
                    'text-sm tabular-nums',
                    expirySoon
                      ? 'font-medium text-destructive'
                      : 'text-muted-foreground',
                  )}
                >
                  {t('layout.expiresAt', { time: expiryTime })}
                </p>
              )}
            </DrawerHeader>
            <DrawerFooter style={safeAreaBottom('1rem')}>
              {showReviewAction && sessionId != null && (
                <Button
                  className="h-12"
                  onClick={() => {
                    close()
                    navigate({ to: '/inventory/count/review' })
                  }}
                >
                  {t('layout.toReview')}
                </Button>
              )}
              <Button
                variant="outline"
                className="h-12"
                onClick={() => {
                  close()
                  navigate({ to: '/inventur' })
                }}
              >
                <Pause className="h-4 w-4" />
                {t('layout.pause')}
              </Button>
              {sessionId != null && (
                <Button
                  variant="outline"
                  className="h-12 text-destructive"
                  onClick={() => setConfirmingAbort(true)}
                >
                  <Trash2 className="h-4 w-4" />
                  {t('layout.abort')}
                </Button>
              )}
              <Button variant="ghost" className="h-12" onClick={close}>
                {t('layout.menuClose')}
              </Button>
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  )
}
